import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Spin, message, Divider } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import axios from "axios";
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import HeadLogo from "../Assets/Group 619.svg";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";
import { checkEmailExists } from "../services/auth";
import { GoogleLogin } from '@react-oauth/google';
import axiosServices from "../services/axios";

const normalizeDetectedCountry = (country) => {
    if (!country) return null;

    const normalizedCountry = String(country).trim();

    if (normalizedCountry === "United States") {
        return "United States of America";
    }

    return normalizedCountry || null;
};

const detectSignupLocation = async () => {
    let country = null;
    let timeZone = null;

    try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (error) {
        console.warn("Could not detect browser timezone.", error);
    }

    try {
        const response = await axios.get("https://ipwho.is/", {
            timeout: 5000,
        });

        if (response.data?.success) {
            country = normalizeDetectedCountry(response.data.country);

            if (!timeZone && response.data.timezone?.id) {
                timeZone = response.data.timezone.id;
            }
        }
    } catch (error) {
        console.warn("Could not detect signup country.", error);
    }

    return {
        country,
        time_zone: timeZone,
    };
};

const GOOGLE_SIGNUP_USER_KEY = "google_signup_user";

const readPendingGoogleUser = () => {
try {
const storedUser = sessionStorage.getItem(
GOOGLE_SIGNUP_USER_KEY
);

if (!storedUser) {
return null;
}

return JSON.parse(storedUser);
} catch (error) {
console.warn(
"Could not restore pending Google signup user.",
error
);
sessionStorage.removeItem(GOOGLE_SIGNUP_USER_KEY);
return null;
}
};

export default function Signup({
googleCompletionMode = false,
}) {
const navigate = useNavigate();
const location = useLocation();

const pendingGoogleUser =
location.state?.googleUser ||
readPendingGoogleUser();

const shouldCompleteGoogleSignup =
googleCompletionMode ||
Boolean(
pendingGoogleUser &&
location.state?.step === 3
);

const [step, setStep] = useState(() =>
shouldCompleteGoogleSignup ? 3 : 1
);

const [details, setDetails] = useState(() => ({
firstname: pendingGoogleUser?.first_name || "",
lastname: pendingGoogleUser?.last_name || "",
email: pendingGoogleUser?.email_id || "",
password: "",
company_name: "",
website: "",
}));

const [mobile, setMobile] = useState();
const [isLoader, setIsLoader] = useState(false);
const [showPassword, setShowPassword] = useState(false);

const antIcon = <LoadingOutlined style={{ fontSize: 24, color: "#fff" }} spin />;

const POST_AUTH_REDIRECT_KEY = "post_auth_redirect";
const AUTH_SOURCE_KEY = "auth_source";

const isSafeInternalRedirect = (value) => {
if (!value) return false;
return value.startsWith("/") && !value.startsWith("//");
};

const saveMiniToolRedirect = (search) => {
const params = new URLSearchParams(search);
const source = params.get("source");
const redirect = params.get("redirect");

if (source === "mini-tools" && isSafeInternalRedirect(redirect)) {
sessionStorage.setItem(AUTH_SOURCE_KEY, source);
sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, redirect);
}
};

const getPostAuthRedirect = (fallback = "/") => {
const redirect = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
return isSafeInternalRedirect(redirect) ? redirect : fallback;
};

const getNationalPhoneNumber = (phone) => {
try {
const parsed = parsePhoneNumber(phone);
return parsed?.nationalNumber || String(phone || "").replace(/\D/g, "");
} catch {
return String(phone || "").replace(/\D/g, "");
}
};

const isFakePhoneNumber = (phone) => {
const nationalNumber = getNationalPhoneNumber(phone);

if (!nationalNumber || nationalNumber.length < 8) return true;

  // Reject repeated digits: 0000000000, 1111111111, etc.
if (/^(\d)\1+$/.test(nationalNumber)) return true;

const blockedNumbers = [
"0000000000",
"1111111111",
"2222222222",
"3333333333",
"4444444444",
"5555555555",
"6666666666",
"7777777777",
"8888888888",
"9999999999",
"1234567890",
"0123456789",
"9876543210",
];

if (blockedNumbers.includes(nationalNumber)) return true;

const ascending = "01234567890123456789";
const descending = "98765432109876543210";

if (ascending.includes(nationalNumber)) return true;
if (descending.includes(nationalNumber)) return true;

return false;
};

const isValidSignupPhone = (phone) => {
if (!phone) return false;
if (!isValidPhoneNumber(phone)) return false;
if (isFakePhoneNumber(phone)) return false;
return true;
};

useEffect(() => {
saveMiniToolRedirect(location.search);

const params = new URLSearchParams(location.search);
const emailFromQuery = params.get("email");

if (shouldCompleteGoogleSignup && pendingGoogleUser) {
sessionStorage.setItem(
GOOGLE_SIGNUP_USER_KEY,
JSON.stringify(pendingGoogleUser)
);

setDetails((prev) => ({
...prev,
firstname: pendingGoogleUser.first_name || "",
lastname: pendingGoogleUser.last_name || "",
email: pendingGoogleUser.email_id || "",
password: "",
}));

setStep(3);
return;
}

if (emailFromQuery) {
setDetails((prev) => ({
...prev,
email: emailFromQuery,
}));
}
}, [
location.search,
pendingGoogleUser,
shouldCompleteGoogleSignup,
]);

const handleGoogleSuccess = async (
credentialResponse
) => {
setIsLoader(true);

try {
const res = await axiosServices.post(
"/user/auth/google",
{
token: credentialResponse.credential,
}
);

const {
token,
user,
isNewUser,
requiresBusinessSetup,
} = res.data.data;

localStorage.setItem("user_Token", token);
localStorage.setItem("Email", user.email_id);
localStorage.setItem("user_id", user._id);

message.success("Authentication successful!");

if (isNewUser || requiresBusinessSetup) {
sessionStorage.setItem(
GOOGLE_SIGNUP_USER_KEY,
JSON.stringify(user)
);

navigate("/complete-google-signup", {
replace: true,
state: {
googleUser: user,
step: 3,
},
});
return;
}

sessionStorage.removeItem(GOOGLE_SIGNUP_USER_KEY);
window.location.href = getPostAuthRedirect("/");
} catch (error) {
toast.error(
error.response?.data?.message ||
"Google Sign-In failed."
);
} finally {
setIsLoader(false);
}
};

const handleGoogleError = () => {
    toast.error("Google authentication failed. Please try again.");
};

const handleInputChange = (e) => {
const { name, value } = e.target;
setDetails((prev) => ({ ...prev, [name]: value }));
};

const handleNextStep = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!details.email.trim() || !emailRegex.test(details.email)) {
        toast.error("Invalid email format.");
        return;
    }
    setIsLoader(true);
    try {
        const emailCheck = await checkEmailExists(details.email);
        if (emailCheck.exists) {
            toast.error("This email is already registered. Please log in.");
        } else {
            setStep(2);
        }
    } catch (error) {
        toast.error("Unable to verify email. Please try again.");
    } finally {
        setIsLoader(false);
    }
};

const handleRegister = async (e) => {
    e.preventDefault();

    if (step === 3) {
        await handleGoogleRegister(e);
        return;
    }

    if (
        !details.firstname ||
        !details.lastname ||
        !details.company_name ||
        !details.website ||
        !details.password ||
        !mobile
    ) {
        toast.error("All fields are required.");
        return;
    }

    if (!details.website.includes(".")) {
        toast.error("Please enter a valid website URL.");
        return;
    }

    if (!isValidSignupPhone(mobile)) {
        toast.error("Please enter a valid phone number.");
        return;
    }

    setIsLoader(true);

    try {
        const locationData = await detectSignupLocation();

        await axios.post(
            `${process.env.REACT_APP_BACKEND_API}/user/register/`,
            {
                first_name: details.firstname,
                last_name: details.lastname,
                email_id: details.email,
                password: details.password,
                mobile,
                company_name: details.company_name,
                website: details.website,
                country: locationData.country,
                time_zone: locationData.time_zone,
            }
        );

        localStorage.setItem("Email", details.email);
        toast.success("Account created! Verifying your email...");
        navigate("/verify-otp");
    } catch (err) {
        toast.error(
            err.response?.data?.message ||
                "Registration failed. Please try again."
        );
    } finally {
        setIsLoader(false);
    }
};

const handleGoogleRegister = async (e) => {
    e.preventDefault();

    if (!details.company_name || !details.website || !mobile) {
        toast.error(
            "Company Name, Website, and Phone Number are required."
        );
        return;
    }

    if (!details.website.includes(".")) {
        toast.error("Please enter a valid website URL.");
        return;
    }

    if (!isValidSignupPhone(mobile)) {
        toast.error("Please enter a valid phone number.");
        return;
    }

    setIsLoader(true);

    try {
        const locationData = await detectSignupLocation();

        await axiosServices.post("/onboarding/add-onboarding", {
            brand_name: details.company_name,
            website: details.website,
            mobile,
            brand_contact: mobile,
            country: locationData.country,
            time_zone: locationData.time_zone,
        });

        sessionStorage.removeItem(
        GOOGLE_SIGNUP_USER_KEY
        );

        toast.success(
        "Brand created! We're analyzing your website in the background."
        );

        window.location.href = "/select-plan";
    } catch (err) {
        toast.error(
            err.response?.data?.message ||
                "Failed to save details. Please try again."
        );
    } finally {
        setIsLoader(false);
    }
};

useEffect(() => {
if (
googleCompletionMode &&
!pendingGoogleUser
) {
toast.error(
"Your Google signup session could not be restored. Please sign in again."
);

localStorage.removeItem("user_Token");
localStorage.removeItem("Email");
localStorage.removeItem("user_id");
sessionStorage.removeItem(
GOOGLE_SIGNUP_USER_KEY
);

navigate("/login", {
replace: true,
});
}
}, [
googleCompletionMode,
navigate,
pendingGoogleUser,
]);


const renderGoogleOnboarding = () => (
    <form onSubmit={handleRegister} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-neutral-muted mb-1">First Name</label>
                <input type="text" name="firstname" className="form-input bg-gray-200" value={details.firstname} readOnly />
            </div>
            <div>
                <label className="block text-sm font-medium text-neutral-muted mb-1">Last Name</label>
                <input type="text" name="lastname" className="form-input bg-gray-200" value={details.lastname} readOnly />
            </div>
        </div>
        <div>
            <label className="block text-sm font-medium text-neutral-muted mb-1">Company Name</label>
            <input type="text" name="company_name" className="form-input" onChange={handleInputChange} autoFocus />
        </div>
        <div>
            <label className="block text-sm font-medium text-neutral-muted mb-1">Company Website</label>
            <input type="text" name="website" placeholder="e.g. oolook.in" className="form-input" onChange={handleInputChange} />
        </div>
        <div>
            <label className="block text-sm font-medium text-neutral-muted mb-1">Phone Number</label>
            <PhoneInput defaultCountry="IN" value={mobile} onChange={setMobile} className="form-input" />
        </div>
        <div className="pt-4">
            <button type="submit" className="btn-primary" disabled={isLoader}>
                {isLoader ? <Spin indicator={antIcon} /> : "Continue"}
            </button>
        </div>
        <p className="text-xs text-center text-neutral-muted px-4">
            By continuing, you agree to the <a href="https://oolook.in/terms" target="_blank" rel="noopener noreferrer" className="link">Terms of Service</a>.
        </p>
    </form>
);

return (
<div className="auth-page">
<ToastContainer position="top-right" autoClose={5000} />

<div className="auth-card auth-shell auth-shell-wide">
<img src={HeadLogo} alt="Oolook Logo" className="mx-auto h-12 w-auto mb-6" />
<h2 className="text-center text-2xl font-bold text-neutral-dark mb-2">
    {step === 1 && "Get Started"}
    {step === 2 && "Tell us about yourself"}
    {step === 3 && "Tell us about your business"}
</h2>
<p className="text-center text-sm text-neutral-muted mb-8">
    {step === 1 && "Create your account to get started."}
    {step === 2 && "Complete your profile to continue."}
    {step === 3 && "We'll use this to set up your brand kit automatically."}
</p>

{step === 1 && (
    <>
        <div className="auth-google">
                        <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            type="standard"
            text="signup_with"
            theme="outline"
            shape="pill"
            size="large"
            width="360"
            logo_alignment="left"
            />
        </div>
        <Divider className="my-6">OR</Divider>
    </>
)}

{step === 3 ? renderGoogleOnboarding() : (
    <form onSubmit={step === 1 ? handleNextStep : handleRegister} className="space-y-4">
        {step === 1 && (
            <div>
                <label className="block text-sm font-medium text-neutral-muted mb-1">Email Address</label>
                <input type="email" name="email" value={details.email} className="form-input" onChange={handleInputChange} autoFocus />
            </div>
        )}
        {step === 2 && (
            <>
                <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-neutral-muted mb-1">First Name</label><input type="text" name="firstname" className="form-input" onChange={handleInputChange} /></div>
                    <div><label className="block text-sm font-medium text-neutral-muted mb-1">Last Name</label><input type="text" name="lastname" className="form-input" onChange={handleInputChange} /></div>
                </div>
                <div><label className="block text-sm font-medium text-neutral-muted mb-1">Company Name</label><input type="text" name="company_name" className="form-input" onChange={handleInputChange} /></div>
                <div><label className="block text-sm font-medium text-neutral-muted mb-1">Company Website</label><input type="text" name="website" placeholder="e.g. oolook.in" className="form-input" onChange={handleInputChange} /></div>
                <div><label className="block text-sm font-medium text-neutral-muted mb-1">Phone Number</label><PhoneInput defaultCountry="IN" value={mobile} onChange={setMobile} className="form-input" /></div>
                <div>
                    <label className="block text-sm font-medium text-neutral-muted mb-1">Password</label>
                    <div className="relative">
                        <input type={showPassword ? "text" : "password"} name="password" className="form-input pr-10" onChange={handleInputChange} />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 px-3 flex items-center text-neutral-muted"><i className={`fa-regular ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i></button>
                    </div>
                </div>
            </>
        )}
        <div className="pt-4"><button type="submit" className="btn-primary" disabled={isLoader}>{isLoader ? <Spin indicator={antIcon} /> : (step === 1 ? "Next" : "Create Account")}</button></div>
        <p className="text-xs text-center text-neutral-muted px-4">By signing up, you agree to the <a href="https://oolook.in/terms" target="_blank" rel="noopener noreferrer" className="link">Terms of Service</a> and <a href="https://oolook.in/privacy" target="_blank" rel="noopener noreferrer" className="link">Privacy Policy</a>.</p>
        {step === 2 && (<button type="button" onClick={() => setStep(1)} className="w-full text-center text-sm text-neutral-muted mt-2 hover:text-primary">Back to Email</button>)}
    </form>
)}

<p className="text-xs text-center text-neutral-muted mt-6">Already have an account? <Link to="/" className="link">Log In</Link></p>
</div>
</div>
);
} 